import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertTriangle, Plus, MapPin, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';
import CadutaForm from '@/components/CadutaForm';
import ReportCadute from '@/components/ReportCadute';
import ReportLesioni from '@/components/ReportLesioni';

const tipoColors = { caduta: 'bg-red-100 text-red-700', lesione_da_pressione: 'bg-orange-100 text-orange-700', agitazione: 'bg-amber-100 text-amber-700', ricovero: 'bg-blue-100 text-blue-700', altro: 'bg-slate-100 text-slate-600' };
const tipoLabels = { caduta: 'Caduta', lesione_da_pressione: 'Lesione da pressione', agitazione: 'Agitazione', ricovero: 'Ricovero', altro: 'Altro' };

export default function EventiAvversi() {
  const [eventi, setEventi] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [filtroArea, setFiltroArea] = useState('tutte');
  const [showForm, setShowForm] = useState(false);
  const [showCaduta, setShowCaduta] = useState(false);
  const [stat, setStat] = useState(null);
  const [form, setForm] = useState({ ospite_id: '', tipo: 'caduta', data_ora: new Date().toISOString().slice(0, 16), luogo: '', dinamica: '', conseguenze: '', interventi: '', follow_up: '' });

  useEffect(() => {
    api.get('/ospiti').then(r => setOspiti(r.data)).catch(() => {});
    api.get('/cadute/statistiche', { params: { giorni: 90 } }).then(r => setStat(r.data)).catch(() => {});
  }, []);

  const ricarica = () => {
    const params = filtroTipo !== 'tutti' ? { tipo: filtroTipo } : {};
    api.get('/eventi', { params }).then(r => setEventi(r.data)).catch(() => {});
    api.get('/cadute/statistiche', { params: { giorni: 90 } }).then(r => setStat(r.data)).catch(() => {});
  };

  useEffect(() => {
    const params = {};
    if (filtroTipo !== 'tutti') params.tipo = filtroTipo;
    setLoading(true);
    api.get('/eventi', { params }).then(r => setEventi(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filtroTipo]);

  const ospitiMap = {};
  ospiti.forEach(o => { ospitiMap[o.id] = o; });

  // Filter by area
  const filteredEventi = filtroArea === 'tutte'
    ? eventi
    : eventi.filter(e => {
        const osp = ospitiMap[e.ospite_id];
        return osp && osp.area === filtroArea;
      });

  const handleSave = async () => {
    if (!form.ospite_id || !form.dinamica) { toast.error('Seleziona ospite e descrivi la dinamica'); return; }
    try {
      await api.post('/eventi', form);
      toast.success('Evento registrato');
      setShowForm(false);
      setForm({ ospite_id: '', tipo: 'caduta', data_ora: new Date().toISOString().slice(0, 16), luogo: '', dinamica: '', conseguenze: '', interventi: '', follow_up: '' });
      api.get('/eventi', { params: filtroTipo !== 'tutti' ? { tipo: filtroTipo } : {} }).then(r => setEventi(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="eventi-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Eventi Avversi</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filteredEventi.length} eventi registrati</p>
        </div>
        <div className="flex items-center gap-2">
        <ReportCadute />
        <ReportLesioni />
        <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => setShowCaduta(true)} data-testid="registra-caduta-global-btn">
          <TrendingDown className="w-4 h-4 mr-2" />Registra Caduta
        </Button>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button data-testid="add-evento-global-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-2" />Segnala Evento</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Segnalazione Evento Avverso</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Ospite *</Label>
                <Select value={form.ospite_id} onValueChange={v => setForm(f => ({ ...f, ospite_id: v }))}>
                  <SelectTrigger data-testid="evento-select-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                  <SelectContent>{ospiti.map(o => <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome} {o.area ? `(${o.area})` : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Tipo</Label>
                  <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(tipoLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Data/Ora</Label><Input type="datetime-local" value={form.data_ora} onChange={e => setForm(f => ({ ...f, data_ora: e.target.value }))} /></div>
              </div>
              <div><Label className="text-xs">Luogo</Label><Input value={form.luogo} onChange={e => setForm(f => ({ ...f, luogo: e.target.value }))} placeholder="Camera, Bagno, Corridoio..." /></div>
              <div><Label className="text-xs">Dinamica *</Label><DictableTextarea data-testid="evento-global-dinamica" value={form.dinamica} onChange={e => setForm(f => ({ ...f, dinamica: e.target.value }))} rows={3} /></div>
              <div><Label className="text-xs">Conseguenze</Label><DictableTextarea value={form.conseguenze} onChange={e => setForm(f => ({ ...f, conseguenze: e.target.value }))} rows={2} /></div>
              <div><Label className="text-xs">Interventi Adottati</Label><DictableTextarea value={form.interventi} onChange={e => setForm(f => ({ ...f, interventi: e.target.value }))} rows={2} /></div>
              <div><Label className="text-xs">Follow-up</Label><Input value={form.follow_up} onChange={e => setForm(f => ({ ...f, follow_up: e.target.value }))} /></div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-evento-global" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <CadutaForm open={showCaduta} onOpenChange={setShowCaduta} ospiti={ospiti} onSaved={ricarica} />

      {stat && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cadute-statistiche">
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Cadute (90 giorni)</p>
            <p className="text-3xl font-bold text-slate-900 font-[Cinzel]" data-testid="stat-cadute-totali">{stat.totale_cadute}</p>
            <p className="text-[11px] text-slate-400">{stat.ospiti_coinvolti} ospiti coinvolti</p>
          </CardContent></Card>
          <Card className={stat.cadute_gravi > 0 ? 'border-red-200 bg-red-50/30' : ''}><CardContent className="p-4">
            <p className="text-xs text-red-600 font-medium uppercase tracking-wider">Con esito grave</p>
            <p className="text-3xl font-bold text-red-700 font-[Cinzel]" data-testid="stat-cadute-gravi">{stat.cadute_gravi}</p>
            <p className="text-[11px] text-slate-400">fratture o trauma cranico</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Indice cadute</p>
            <p className="text-3xl font-bold text-slate-900 font-[Cinzel]" data-testid="stat-indice">{stat.indice_per_1000_giornate}</p>
            <p className="text-[11px] text-slate-400">per 1000 giornate di degenza</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Ospiti recidivi</p>
            <p className="text-3xl font-bold text-slate-900 font-[Cinzel]" data-testid="stat-recidivi">{stat.recidivi.length}</p>
            <p className="text-[11px] text-slate-400 truncate">{stat.recidivi.slice(0, 2).map(r => `${r.ospite_nome} (${r.cadute})`).join(', ') || '2 o piu cadute nel periodo'}</p>
          </CardContent></Card>
          {stat.per_luogo.length > 0 && (
            <Card className="col-span-2 lg:col-span-4"><CardContent className="p-4 flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Luoghi:</span>
                {stat.per_luogo.slice(0, 6).map(l => <Badge key={l.luogo} variant="outline" className="text-[10px]">{l.luogo} · {l.n}</Badge>)}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Turni:</span>
                {stat.per_turno.map(t => <Badge key={t.turno} variant="outline" className="text-[10px] capitalize">{t.turno} · {t.n}</Badge>)}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Gravita:</span>
                {stat.per_gravita.map(g => <Badge key={g.valore} variant="outline" className="text-[10px]">{g.label} · {g.n}</Badge>)}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Filtri */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger data-testid="eventi-filter-tipo" className="w-52 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutti">Tutti i tipi</SelectItem>{Object.entries(tipoLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-slate-400" />
          <button onClick={() => setFiltroArea('tutte')} className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${filtroArea === 'tutte' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`} data-testid="eventi-area-tutte">Tutte</button>
          <button onClick={() => setFiltroArea('verde')} className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${filtroArea === 'verde' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-600 border-emerald-200'}`} data-testid="eventi-area-verde">Verde</button>
          <button onClick={() => setFiltroArea('gialla')} className={`px-2.5 py-1 text-[10px] rounded-full border transition-all ${filtroArea === 'gialla' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-600 border-amber-200'}`} data-testid="eventi-area-gialla">Gialla</button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-24 p-4" /></Card>)}</div>
      ) : filteredEventi.length === 0 ? (
        <div className="text-center py-16"><AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nessun evento registrato{filtroArea !== 'tutte' ? ` per area ${filtroArea}` : ''}</p></div>
      ) : (
        <div className="space-y-3" data-testid="eventi-list">
          {filteredEventi.map(e => {
            const osp = ospitiMap[e.ospite_id];
            return (
              <Card key={e.id} className="animate-fade-in">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{osp ? `${osp.cognome} ${osp.nome}` : 'Ospite'}</span>
                        <Badge className={`text-[10px] ${tipoColors[e.tipo] || ''}`}>{tipoLabels[e.tipo] || e.tipo}</Badge>
                        {osp?.area && <Badge variant="outline" className={`text-[9px] capitalize ${osp.area === 'verde' ? 'border-emerald-300 text-emerald-600' : 'border-amber-300 text-amber-600'}`}>{osp.area}</Badge>}
                        <span className="text-xs text-slate-400">{new Date(e.data_ora).toLocaleString('it-IT')}</span>
                        {e.luogo && <span className="text-xs text-slate-400">- {e.luogo}</span>}
                      </div>
                      <p className="text-sm text-slate-700">{e.dinamica}</p>
                      {e.conseguenze && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Conseguenze:</span> {e.conseguenze}</p>}
                      {e.interventi && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Interventi:</span> {e.interventi}</p>}
                      {e.follow_up && <p className="text-xs text-brand mt-1"><span className="font-medium">Follow-up:</span> {e.follow_up}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs text-slate-400">{e.operatore_nome}</span>
                      {osp && <p className="text-[10px] text-slate-300">Cam. {osp.stanza}/{osp.letto}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
